import { useEffect, useId, useState } from 'react'

import Icon from '@components/utils/Icon'
import { Avatar } from '@components/atoms/Avatar'

import { scopedTranslation } from '@utils/I18n'

import useSupporterSearchQuery from '../assign_shifts/useSupporterSearchQuery'
import { useShiftsStore } from './stores/useShiftsStore'

const t = scopedTranslation('roster_board')

const SEARCH_DEBOUNCE_MS = 200

export type PickedSupporter = { id: string }

type Result = { id: string; fullName: string }

export function AssigneeSelect({ onPick }: { onPick: (supporter: PickedSupporter) => void }) {
  const [query, setQuery] = useState('')
  const inputId = useId()
  const [results, setResults] = useState<Result[]>([])
  const [loading, setLoading] = useState(false)
  const { querySupporter } = useSupporterSearchQuery()
  const groupId = useShiftsStore((state) => state.groupId)

  const trimmed = query.trim()

  useEffect(() => {
    if (trimmed === '') {
      setResults([])
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)
    const handle = setTimeout(() => {
      querySupporter({ nameInput: trimmed, groupId })
        .then((res) => {
          if (!active) return
          setResults(res.supporters.map((s) => ({ id: s.id, fullName: s.label })))
          setLoading(false)
        })
        .catch(() => {
          if (!active) return
          setResults([])
          setLoading(false)
        })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      active = false
      clearTimeout(handle)
    }
  }, [trimmed, groupId])

  const status = (message: string) => (
    <p className="tw:px-2 tw:py-3 tw:text-center tw:text-xs tw:text-gray-400">{message}</p>
  )

  return (
    <div>
      <label htmlFor={inputId} className="tw:mb-1 tw:block tw:text-sm tw:font-medium tw:text-gray-700">
        {t('picker_label')}
      </label>
      <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-gray-300 tw:px-2 tw:py-1.5 tw:focus-within:border-primary-400">
        <Icon type="search-lg" width={16} height={16} className="tw:text-gray-400" aria-hidden />
        <input
          id={inputId}
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="tw:w-full tw:bg-transparent tw:text-sm tw:text-gray-900 tw:placeholder-gray-400 tw:focus:outline-hidden"
        />
      </div>
      <div className="tw:max-h-48 tw:overflow-y-auto">
        {trimmed === ''
          ? status(t('picker_hint'))
          : loading
            ? status(t('picker_loading'))
            : results.length === 0
              ? status(t('picker_no_matches'))
              : results.map((supporter) => (
                  <button
                    key={supporter.id}
                    type="button"
                    onClick={() => onPick({ id: supporter.id })}
                    className="tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-lg tw:px-2 tw:py-1.5 tw:text-left tw:text-sm tw:text-gray-700 tw:hover:bg-primary-50 tw:hover:text-primary-700"
                  >
                    <Avatar name={supporter.fullName} />
                    {supporter.fullName}
                  </button>
                ))}
      </div>
    </div>
  )
}
